import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText, RefreshCw, Search, Download, ExternalLink,
  Image, FileSpreadsheet, File, CheckCircle, Clock,
  HardDrive, ChevronDown, ChevronRight, Filter,
  ArrowUpDown, FolderOpen, Layers,
} from 'lucide-react';
import { companiesApi } from '../api';
import PageInfoButton from '@/components/PageInfoButton';

/* ─── Types ──────────────────────────────────────────────────────────────── */

interface DocumentItem {
  id: number;
  compliance_page_id: number;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  mime_type: string;
  notes?: string;
  status: 'draft' | 'submitted';
  created_at: string;
  submitted_at?: string;
  uploader_name: string;
  page_title: string;
  page_section: string;
  framework_name?: string;
}

type FilterStatus = 'all' | 'submitted' | 'draft';
type SortKey = 'date' | 'name' | 'size';
type ViewMode = 'grouped' | 'flat';

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function formatFileSize(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
}

function fileExt(name: string) {
  return name.split('.').pop()?.toUpperCase() || 'FILE';
}

function extColor(mime: string, type: string) {
  if (type === 'image' || mime.startsWith('image/'))
    return { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' };
  if (mime.includes('pdf'))
    return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' };
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv'))
    return { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' };
  if (mime.includes('word') || mime.includes('document'))
    return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' };
  if (mime.includes('zip') || mime.includes('compressed'))
    return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' };
  return { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' };
}

function FileTypeIcon({ mime, type, size = 'md' }: { mime: string; type: string; size?: 'sm' | 'md' }) {
  const sz = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  if (type === 'image' || mime.startsWith('image/')) return <Image className={`${sz} text-purple-600`} />;
  if (mime.includes('pdf')) return <FileText className={`${sz} text-red-600`} />;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv'))
    return <FileSpreadsheet className={`${sz} text-emerald-600`} />;
  return <File className={`${sz} text-slate-500`} />;
}

function UploaderAvatar({ name }: { name: string }) {
  const initials = name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  const hue = (name.charCodeAt(0) * 37 + (name.charCodeAt(1) || 0) * 13) % 360;
  const style = { background: `hsl(${hue},65%,88%)`, color: `hsl(${hue},55%,35%)` };
  return (
    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0" style={style}>
      {initials || '?'}
    </div>
  );
}

/* ─── Document Row ───────────────────────────────────────────────────────── */

const DocRow: React.FC<{ doc: DocumentItem; showSection?: boolean }> = ({ doc, showSection = true }) => {
  const clr = extColor(doc.mime_type, doc.file_type);
  return (
    <div className="group flex items-center gap-3 px-4 py-3 hover:bg-purple-50/30 transition-colors border-b border-slate-100 last:border-0">
      {/* File type badge */}
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg border flex flex-col items-center justify-center gap-0.5 ${clr.bg} ${clr.border}`}>
        <FileTypeIcon mime={doc.mime_type} type={doc.file_type} size="sm" />
        <span className={`text-[8px] font-bold leading-none ${clr.text}`}>{fileExt(doc.file_name)}</span>
      </div>

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{doc.file_name}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {showSection && (
            <span className="text-[11px] text-slate-400 truncate max-w-[180px]">
              {doc.page_section} · {doc.page_title}
            </span>
          )}
          {doc.notes && (
            <span className="text-[11px] text-slate-400 italic truncate max-w-[140px]">"{doc.notes}"</span>
          )}
        </div>
      </div>

      {/* Uploader */}
      <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0 w-28">
        <UploaderAvatar name={doc.uploader_name} />
        <span className="text-[11px] text-slate-500 truncate">{doc.uploader_name.split(' ')[0]}</span>
      </div>

      {/* Size */}
      <span className="hidden md:block text-[11px] text-slate-400 w-14 text-right flex-shrink-0">
        {formatFileSize(doc.file_size)}
      </span>

      {/* Date */}
      <span className="hidden lg:block text-[11px] text-slate-400 w-20 text-right flex-shrink-0">
        {new Date(doc.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
      </span>

      {/* Status badge */}
      <div className="flex-shrink-0">
        {doc.status === 'submitted' ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
            <CheckCircle className="w-2.5 h-2.5" /> Submitted
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
            <Clock className="w-2.5 h-2.5" /> Draft
          </span>
        )}
      </div>

      {/* Actions — hover reveal */}
      <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <a
          href={doc.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
          title="View"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <a
          href={doc.file_url}
          download
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          title="Download"
        >
          <Download className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
};

/* ─── Group Card ─────────────────────────────────────────────────────────── */

const GroupCard: React.FC<{ section: string; docs: DocumentItem[] }> = ({ section, docs }) => {
  const [open, setOpen] = useState(true);
  const submitted = docs.filter(d => d.status === 'submitted').length;
  const pct = docs.length > 0 ? Math.round((submitted / docs.length) * 100) : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50/60/60 transition-colors text-left"
      >
        <div className="w-7 h-7 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
          <FolderOpen className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{section}</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="h-1.5 rounded-full overflow-hidden bg-slate-200 w-24">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[10px] text-slate-400">{submitted}/{docs.length} submitted</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs font-semibold text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
            {docs.length} file{docs.length !== 1 ? 's' : ''}
          </span>
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div>
          {/* Column headers */}
          <div className="flex items-center gap-3 px-4 py-1.5 bg-white border-b border-slate-100">
            <div className="w-9 flex-shrink-0" />
            <span className="flex-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">File</span>
            <span className="hidden sm:block text-[10px] font-bold text-slate-400 uppercase tracking-wider w-28">Uploaded by</span>
            <span className="hidden md:block text-[10px] font-bold text-slate-400 uppercase tracking-wider w-14 text-right">Size</span>
            <span className="hidden lg:block text-[10px] font-bold text-slate-400 uppercase tracking-wider w-20 text-right">Date</span>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider w-20">Status</span>
            <div className="w-14" />
          </div>
          {docs.map(d => <DocRow key={d.id} doc={d} showSection={false} />)}
        </div>
      )}
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */

const CompanyDocuments: React.FC = () => {
  const [allDocs, setAllDocs] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const raw = await companiesApi.getCompanyEvidence();
      setAllDocs(raw.map((item: any) => ({
        id: item.id,
        compliance_page_id: item.compliance_page_id,
        file_name: item.file_name,
        file_url: item.file_url,
        file_type: item.file_type,
        file_size: item.file_size,
        mime_type: item.mime_type,
        notes: item.notes,
        status: item.status,
        created_at: item.created_at,
        submitted_at: item.submitted_at,
        uploader_name: `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Unknown',
        page_title: item.page_title || 'Unknown Page',
        page_section: item.page_section || 'Unknown Section',
        framework_name: item.framework_name,
      })));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const cycleSort = (key: SortKey) => {
    if (sort === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSort(key); setSortDir('desc'); }
  };

  /* ─── Stats (always from full dataset) ─────────────────────────────────── */
  const totalSize = allDocs.reduce((s, d) => s + d.file_size, 0);
  const submittedCount = allDocs.filter(d => d.status === 'submitted').length;
  const draftCount = allDocs.filter(d => d.status === 'draft').length;

  /* ─── Filtered + sorted list ────────────────────────────────────────────── */
  const visible = useMemo(() => {
    let list = allDocs.filter(d => {
      if (filterStatus !== 'all' && d.status !== filterStatus) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          d.file_name.toLowerCase().includes(q) ||
          d.page_title.toLowerCase().includes(q) ||
          d.page_section.toLowerCase().includes(q) ||
          d.uploader_name.toLowerCase().includes(q) ||
          (d.framework_name || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sort === 'name') cmp = a.file_name.localeCompare(b.file_name);
      else if (sort === 'size') cmp = a.file_size - b.file_size;
      else cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [allDocs, filterStatus, search, sort, sortDir]);

  /* ─── Grouped by section ────────────────────────────────────────────────── */
  const groups = useMemo(() => {
    const map = new Map<string, DocumentItem[]>();
    visible.forEach(d => {
      const key = d.page_section;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    });
    return Array.from(map.entries()).map(([section, docs]) => ({ section, docs }));
  }, [visible]);

  /* ─── Loading ───────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  /* ─── Render ────────────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Hero Header ─────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-purple-100/70 shadow-sm">
        <div className="max-w-7xl mx-auto px-8 py-7">
          <div className="flex items-center justify-between mb-6">

            {/* Left: icon + title */}
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-300/40 flex-shrink-0">
                <FileText className="text-white" size={30} />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 tracking-tight">Document Library</h1><PageInfoButton /></div>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full border border-purple-200 shadow-sm">
                    {allDocs.length} files
                  </span>
                  {submittedCount > 0 && (
                    <span className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
                      <CheckCircle className="w-3 h-3" />
                      {submittedCount} submitted
                    </span>
                  )}
                </div>
                <p className="text-slate-500 text-sm">All compliance documents uploaded by your team</p>
              </div>
            </div>

            {/* Right: refresh */}
            <button
              onClick={load}
              className="group bg-white border border-slate-200 text-slate-700 px-5 py-3 rounded-xl font-bold hover:border-purple-300 hover:bg-purple-50 transition-all shadow-sm hover:shadow-md flex items-center gap-2"
            >
              <RefreshCw size={18} className="group-hover:rotate-180 transition-transform duration-500" />
              Refresh
            </button>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              {
                label: 'Total Files', value: allDocs.length,
                icon: <FileText size={22} />,
                gradient: 'from-purple-500 to-indigo-600', shadow: 'shadow-purple-200',
                sub: `${groups.length} section${groups.length !== 1 ? 's' : ''}`,
                bar: allDocs.length > 0 ? 1 : 0, barColor: 'bg-purple-400',
              },
              {
                label: 'Submitted', value: submittedCount,
                icon: <CheckCircle size={22} />,
                gradient: 'from-emerald-500 to-teal-500', shadow: 'shadow-emerald-200',
                sub: allDocs.length > 0 ? `${Math.round((submittedCount / allDocs.length) * 100)}% complete` : '—',
                bar: allDocs.length > 0 ? submittedCount / allDocs.length : 0, barColor: 'bg-emerald-400',
              },
              {
                label: 'Draft', value: draftCount,
                icon: <Clock size={22} />,
                gradient: 'from-amber-500 to-orange-500', shadow: 'shadow-amber-200',
                sub: draftCount > 0 ? 'Awaiting submission' : 'All submitted',
                bar: allDocs.length > 0 ? draftCount / allDocs.length : 0, barColor: 'bg-amber-400',
              },
              {
                label: 'Total Size', value: formatFileSize(totalSize),
                icon: <HardDrive size={22} />,
                gradient: 'from-blue-500 to-cyan-500', shadow: 'shadow-blue-200',
                sub: 'Across all files', isText: true,
                bar: 0.6, barColor: 'bg-blue-400',
              },
            ].map(card => (
              <div key={card.label} className="bg-white rounded-2xl p-5 border border-slate-200/80 hover:border-purple-200 transition-all hover:shadow-lg group">
                <div className="flex items-center gap-4 mb-3">
                  <div className={`w-12 h-12 ${card.gradient} rounded-xl flex items-center justify-center text-white shadow-md ${card.shadow} group-hover:scale-105 transition-transform`}>
                    {card.icon}
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{card.label}</p>
                    <p className={`${(card as any).isText ? 'text-2xl' : 'text-3xl'} font-bold text-slate-800 leading-none mt-0.5`}>{card.value}</p>
                  </div>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${card.barColor} rounded-full transition-all duration-700`} style={{ width: `${Math.min(card.bar * 100, 100)}%` }} />
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5 font-medium">{card.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-8 py-8">

        {/* ── Search & Filter toolbar ──────────────────────────────────────── */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden mb-6">
          <div className="bg-slate-50 px-6 py-4 border-b border-purple-100">
            <div className="flex items-center gap-3">
              {/* Search input */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search documents, sections, uploaders…"
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-100 focus:border-purple-400 transition-all text-sm bg-white placeholder:text-slate-400"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold bg-slate-100 hover:bg-slate-200 px-2 py-0.5 rounded-md transition-all"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Status filter pills */}
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1 shadow-sm flex-shrink-0">
                {([
                  { key: 'all', label: `All (${allDocs.length})` },
                  { key: 'submitted', label: `Submitted (${submittedCount})` },
                  { key: 'draft', label: `Draft (${draftCount})` },
                ] as { key: FilterStatus; label: string }[]).map(f => (
                  <button
                    key={f.key}
                    onClick={() => setFilterStatus(f.key)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                      filterStatus === f.key
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Sort controls */}
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1 shadow-sm flex-shrink-0">
                <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 ml-1.5" />
                {([
                  { key: 'date', label: 'Date' },
                  { key: 'name', label: 'Name' },
                  { key: 'size', label: 'Size' },
                ] as { key: SortKey; label: string }[]).map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => cycleSort(opt.key)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                      sort === opt.key
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}{sort === opt.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                ))}
              </div>

              {/* View toggle */}
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1 shadow-sm flex-shrink-0">
                <button
                  onClick={() => setViewMode('grouped')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${viewMode === 'grouped' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
                >
                  <Layers className="w-3 h-3" /> Grouped
                </button>
                <button
                  onClick={() => setViewMode('flat')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${viewMode === 'flat' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
                >
                  <Filter className="w-3 h-3" /> Flat
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Content ──────────────────────────────────────────────────────── */}
        {visible.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl py-20 text-center shadow-sm">
            <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="font-bold text-slate-700 text-lg">
              {search || filterStatus !== 'all' ? 'No documents match your filters' : 'No documents yet'}
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              {search || filterStatus !== 'all' ? 'Try adjusting your search or filter.' : 'Documents will appear here once uploaded by your team.'}
            </p>
          </div>
        ) : viewMode === 'grouped' ? (
          <div className="space-y-3">
            {groups.map(({ section, docs }) => (
              <GroupCard key={section} section={section} docs={docs} />
            ))}
          </div>
        ) : (
          /* Flat view */
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="flex items-center gap-3 px-4 py-2.5 bg-purple-600 border-b border-purple-700">
              <div className="w-9 flex-shrink-0" />
              <span className="flex-1 text-[10px] font-bold text-purple-100 uppercase tracking-wider">File</span>
              <span className="hidden sm:block text-[10px] font-bold text-purple-100 uppercase tracking-wider w-28">Uploaded by</span>
              <span className="hidden md:block text-[10px] font-bold text-purple-100 uppercase tracking-wider w-14 text-right">Size</span>
              <span className="hidden lg:block text-[10px] font-bold text-purple-100 uppercase tracking-wider w-20 text-right">Date</span>
              <span className="text-[10px] font-bold text-purple-100 uppercase tracking-wider w-20">Status</span>
              <div className="w-14" />
            </div>
            {visible.map(d => <DocRow key={d.id} doc={d} showSection />)}
          </div>
        )}

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        {visible.length > 0 && (
          <div className="mt-4 bg-purple-50 border border-purple-100 rounded-2xl px-6 py-3 flex items-center justify-between">
            <span className="text-xs text-slate-500 font-medium">
              Showing <span className="font-bold text-purple-700">{visible.length}</span> of <span className="font-bold text-slate-700">{allDocs.length}</span> documents
            </span>
            <span className="text-xs text-slate-500 font-medium">
              <span className="font-bold text-emerald-600">{submittedCount}</span> submitted · <span className="font-bold text-amber-600">{draftCount}</span> draft · <span className="font-bold text-blue-600">{formatFileSize(totalSize)}</span> total
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDocuments;
